import { useState, useEffect, useCallback } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import AppLayout from '../../components/layout/AppLayout';
import { Spinner, Modal } from '../../components/ui';
import { providerProfileApi } from '../../api';
import useProviderProfileStore from '../../stores/providerProfileStore';
import type { TcrPaymentsResponse, TcrPaymentStaffGroup } from '../../types/providerTcrPayments';

// ─── Helpers ──────────────────────────────────────────────────

/** Get the Monday of the week containing `date` */
const getMonday = (date: Date): Date => {
	const d = new Date(date);
	const day = d.getDay();
	d.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
	d.setHours(0, 0, 0, 0);
	return d;
};

/** Format date as YYYY-MM-DD for the API */
const toDateString = (d: Date): string => {
	const year = d.getFullYear();
	const month = String(d.getMonth() + 1).padStart(2, '0');
	const day = String(d.getDate()).padStart(2, '0');
	return `${year}-${month}-${day}`;
};

/** Format date for display: "Mon, Apr 14" */
const formatShortDate = (d: Date): string => {
	const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
	const months = [
		'Jan',
		'Feb',
		'Mar',
		'Apr',
		'May',
		'Jun',
		'Jul',
		'Aug',
		'Sep',
		'Oct',
		'Nov',
		'Dec',
	];
	return `${days[d.getDay()]}, ${months[d.getMonth()]} ${d.getDate()}`;
};

/** Get week label relative to current week */
const getWeekLabel = (monday: Date): string => {
	const today = new Date();
	const currentMonday = getMonday(today);
	const diff = Math.round(
		(monday.getTime() - currentMonday.getTime()) / (7 * 24 * 60 * 60 * 1000)
	);
	if (diff === 0) return 'This Week';
	if (diff === -1) return 'Last Week';
	if (diff === 1) return 'Next Week';
	if (diff < -1) return `${Math.abs(diff)} Weeks Ago`;
	return `${diff} Weeks Ahead`;
};

const formatCurrency = (amount: number): string => `$${amount.toFixed(2)}`;

const formatDateTime = (iso: string): string => {
	const d = new Date(iso);
	const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
	const months = [
		'Jan',
		'Feb',
		'Mar',
		'Apr',
		'May',
		'Jun',
		'Jul',
		'Aug',
		'Sep',
		'Oct',
		'Nov',
		'Dec',
	];
	return `${days[d.getDay()]}, ${months[d.getMonth()]} ${d.getDate()}`;
};

// ─── Summary Card ────────────────────────────────────────────

interface SummaryCardProps {
	icon: string;
	value: string | number;
	label: string;
	colorClass?: string;
	highlight?: boolean;
}

const SummaryCard = ({ icon, value, label, colorClass = '', highlight }: SummaryCardProps) => (
	<div
		className={`tcr-payments__summary-card${highlight ? ' tcr-payments__summary-card--highlight' : ''}`}
	>
		<div className="tcr-payments__summary-icon">{icon}</div>
		<div className={`tcr-payments__summary-value ${colorClass}`}>{value}</div>
		<div className="tcr-payments__summary-label">{label}</div>
	</div>
);

// ─── Main Component ──────────────────────────────────────────

const TCRPayments = () => {
	const { selectedLocationId } = useProviderProfileStore();

	// Week navigation
	const [currentMonday, setCurrentMonday] = useState<Date>(() => getMonday(new Date()));

	// Data
	const [data, setData] = useState<TcrPaymentsResponse | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	// Expanded staff rows
	const [expandedStaff, setExpandedStaff] = useState<Set<number>>(new Set());

	// Pay modal
	const [payModal, setPayModal] = useState<{
		staffName: string;
		label: string;
		netPayable: number;
		subscriptionDeduction: number;
		serviceAmount: number;
	} | null>(null);

	// ─── Fetch Data ─────────────────────────────────────────

	const fetchData = useCallback(async () => {
		if (!selectedLocationId) return;
		setIsLoading(true);
		setError(null);
		try {
			const result = await providerProfileApi.getTcrPayments(
				selectedLocationId,
				toDateString(currentMonday)
			);
			setData(result);
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to load TCR payments');
		} finally {
			setIsLoading(false);
		}
	}, [selectedLocationId, currentMonday]);

	useEffect(() => {
		fetchData();
	}, [fetchData]);

	// ─── Week Navigation ────────────────────────────────────

	const changeWeek = (direction: -1 | 1) => {
		setExpandedStaff(new Set());
		setCurrentMonday(prev => {
			const next = new Date(prev);
			next.setDate(next.getDate() + direction * 7);
			return next;
		});
	};

	const sunday = new Date(currentMonday);
	sunday.setDate(currentMonday.getDate() + 6);

	// ─── Toggle Expand ──────────────────────────────────────

	const toggleStaff = (staffId: number) => {
		setExpandedStaff(prev => {
			const next = new Set(prev);
			if (next.has(staffId)) next.delete(staffId);
			else next.add(staffId);
			return next;
		});
	};

	// ─── Pay Modal ──────────────────────────────────────────

	const openPayModal = (group: TcrPaymentStaffGroup) => {
		setPayModal({
			staffName: group.staffName,
			label: group.tcrCount === 1 ? group.tcrs[0]?.tcrNumber : 'All TCRs',
			netPayable: group.netPayable,
			subscriptionDeduction: group.subscriptionDeduction,
			serviceAmount: group.totalServiceAmount,
		});
	};

	// ─── Render ─────────────────────────────────────────────

	if (!selectedLocationId) {
		return (
			<AppLayout>
				<div className="flex items-center justify-center h-64 text-gray-500 dark:text-gray-400">
					Please select a location to view TCR payments.
				</div>
			</AppLayout>
		);
	}

	return (
		<AppLayout>
			<div className="tcr-payments">
				{/* Page Header */}
				<div className="tcr-payments__header">
					<h1 className="tcr-payments__title">TCR Pending Payments</h1>
					<p className="tcr-payments__subtitle">
						Review and process weekly staff payments from approved Task Completion
						Reports
					</p>
				</div>

				{/* Week Navigation */}
				<div className="tcr-payments__week-nav">
					<button
						className="tcr-payments__week-btn cursor-pointer"
						onClick={() => changeWeek(-1)}
					>
						<ChevronLeftIcon className="w-4 h-4" />
						<span className="hidden sm:inline">Previous</span>
					</button>
					<div className="tcr-payments__week-center">
						<div className="tcr-payments__week-label">
							{getWeekLabel(currentMonday)}
						</div>
						<div className="tcr-payments__week-dates">
							{formatShortDate(currentMonday)} — {formatShortDate(sunday)},{' '}
							{sunday.getFullYear()}
						</div>
					</div>
					<button
						className="tcr-payments__week-btn cursor-pointer"
						onClick={() => changeWeek(1)}
					>
						<span className="hidden sm:inline">Next</span>
						<ChevronRightIcon className="w-4 h-4" />
					</button>
				</div>

				{isLoading ? (
					<div className="flex items-center justify-center py-20">
						<Spinner size="lg" />
					</div>
				) : error ? (
					<div className="text-center py-20 text-red-500 dark:text-red-400">
						<p className="font-semibold mb-2">Failed to load data</p>
						<p className="text-sm">{error}</p>
						<button
							onClick={fetchData}
							className="mt-4 text-sm text-indigo-600 dark:text-indigo-400 hover:underline cursor-pointer"
						>
							Try again
						</button>
					</div>
				) : data ? (
					<>
						{/* Summary Cards */}
						<div className="tcr-payments__summary-grid">
							<SummaryCard
								icon="📋"
								value={data.totalTcrCount}
								label="Total TCRs"
								colorClass="tcr-payments__summary-value--total"
							/>
							<SummaryCard
								icon="⏳"
								value={data.pendingPaymentCount}
								label="Pending Payment"
								colorClass="tcr-payments__summary-value--pending"
							/>
							<SummaryCard
								icon="✅"
								value={data.paidCount}
								label="Paid"
								colorClass="tcr-payments__summary-value--paid"
							/>
							<SummaryCard
								icon="💎"
								value={formatCurrency(data.totalSubscriptionDeductions)}
								label="Subscription Deductions"
								colorClass="tcr-payments__summary-value--fee"
							/>
							<SummaryCard
								icon="💰"
								value={formatCurrency(data.weeklyTotalPayable)}
								label="Weekly Total Payable"
								highlight
							/>
						</div>

						{data.staffGroups.length === 0 ? (
							<div className="text-center py-16 text-gray-500 dark:text-gray-400">
								<p className="text-lg font-semibold mb-2">
									No approved TCRs this week
								</p>
								<p className="text-sm">
									Check back later or navigate to a different week.
								</p>
							</div>
						) : (
							<>
								{/* Desktop Table */}
								<div className="tcr-payments__table-container">
									<table className="tcr-payments__table">
										<thead>
											<tr>
												<th style={{ width: 40 }}></th>
												<th>Staff</th>
												<th>Total Service</th>
												<th>Subscription Fee</th>
												<th>Net Payable</th>
												<th style={{ color: '#9ca3af' }}>Action</th>
											</tr>
										</thead>
										<tbody>
											{data.staffGroups.map(group => {
												const isExpanded = expandedStaff.has(group.staffId);
												return (
													<>
														{/* Staff summary row */}
														<tr
															key={`staff-${group.staffId}`}
															className="tcr-payments__staff-row"
															onClick={() =>
																toggleStaff(group.staffId)
															}
														>
															<td>
																<ChevronRightIcon
																	className={`w-4 h-4 text-indigo-500 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
																/>
															</td>
															<td>
																<div className="tcr-payments__staff-info">
																	<div
																		className="tcr-payments__staff-avatar"
																		style={{
																			background:
																				group.allPaid
																					? '#10b981'
																					: '#f59e0b',
																		}}
																	>
																		{group.tcrCount}
																	</div>
																	<div>
																		<div className="tcr-payments__staff-name">
																			{group.staffName}
																		</div>
																		<div className="tcr-payments__staff-salon">
																			{group.locationName}
																		</div>
																	</div>
																</div>
															</td>
															<td>
																<span className="tcr-payments__amount tcr-payments__amount--service">
																	{formatCurrency(
																		group.totalServiceAmount
																	)}
																</span>
															</td>
															<td>
																<span className="tcr-payments__amount tcr-payments__amount--fee">
																	{formatCurrency(
																		group.subscriptionDeduction
																	)}
																</span>
															</td>
															<td>
																<span className="tcr-payments__amount tcr-payments__amount--net">
																	{formatCurrency(
																		group.netPayable
																	)}
																</span>
															</td>
															<td>
																{group.allPaid ? (
																	<span className="tcr-payments__paid-label">
																		✅ Paid
																	</span>
																) : (
																	<button
																		className="tcr-payments__pay-btn cursor-pointer"
																		onClick={e => {
																			e.stopPropagation();
																			openPayModal(group);
																		}}
																	>
																		💰{' '}
																		{group.tcrCount > 1
																			? 'Pay All'
																			: 'Pay'}
																	</button>
																)}
															</td>
														</tr>

														{/* Expanded TCR detail rows */}
														{isExpanded &&
															group.tcrs.map(tcr => (
																<tr
																	key={`tcr-${tcr.tcrId}`}
																	className="tcr-payments__detail-row"
																>
																	<td></td>
																	<td style={{ paddingLeft: 62 }}>
																		<span className="tcr-payments__tcr-id">
																			{tcr.tcrNumber}
																		</span>
																		<div className="text-xs text-gray-400 mt-0.5">
																			{formatDateTime(
																				tcr.reviewedAt
																			)}
																		</div>
																	</td>
																	<td>
																		<span className="tcr-payments__amount tcr-payments__amount--service">
																			{formatCurrency(
																				tcr.serviceAmount
																			)}
																		</span>
																	</td>
																	<td>
																		<span className="tcr-payments__amount tcr-payments__amount--fee">
																			—
																		</span>
																	</td>
																	<td>
																		<span className="tcr-payments__amount tcr-payments__amount--net">
																			{formatCurrency(
																				tcr.netPayable
																			)}
																		</span>
																	</td>
																	<td>
																		{tcr.paymentStatus ===
																		'PAID' ? (
																			<span className="tcr-payments__paid-label">
																				✅{' '}
																				{tcr.payoutMethod
																					? `via ${tcr.payoutMethod}`
																					: 'Paid'}
																			</span>
																		) : (
																			<span className="text-xs text-gray-400 font-medium">
																				Pending
																			</span>
																		)}
																	</td>
																</tr>
															))}

														{/* Subscription deductions row (when expanded) */}
														{isExpanded &&
															group.subscriptionDeductionItems
																.length > 0 &&
															group.subscriptionDeductionItems.map(
																item => (
																	<tr
																		key={`deduction-${item.deductionId}`}
																		className="tcr-payments__detail-row"
																	>
																		<td></td>
																		<td
																			style={{
																				paddingLeft: 62,
																			}}
																		>
																			<span className="text-xs text-purple-600 dark:text-purple-400 font-semibold">
																				{item.description}
																			</span>
																		</td>
																		<td></td>
																		<td>
																			<span className="tcr-payments__amount tcr-payments__amount--fee">
																				{formatCurrency(
																					item.amount
																				)}
																			</span>
																		</td>
																		<td></td>
																		<td></td>
																	</tr>
																)
															)}
													</>
												);
											})}
										</tbody>
									</table>
								</div>

								{/* Mobile Cards */}
								<div className="tcr-payments__mobile-list">
									{data.staffGroups.map(group => {
										const isExpanded = expandedStaff.has(group.staffId);
										return (
											<div
												key={group.staffId}
												className="tcr-payments__mobile-card"
											>
												<div
													className="tcr-payments__mobile-header"
													onClick={() => toggleStaff(group.staffId)}
												>
													<div
														className="tcr-payments__staff-avatar"
														style={{
															background: group.allPaid
																? '#10b981'
																: '#f59e0b',
														}}
													>
														{group.tcrCount}
													</div>
													<div className="tcr-payments__mobile-details">
														<div className="tcr-payments__mobile-name">
															{group.staffName}
														</div>
														<div className="tcr-payments__mobile-salon">
															{group.locationName}
														</div>
													</div>
													<div className="tcr-payments__mobile-right">
														<div className="tcr-payments__mobile-amount">
															{formatCurrency(group.netPayable)}
														</div>
														{group.subscriptionDeduction > 0 && (
															<div className="tcr-payments__mobile-fee">
																Fee:{' '}
																{formatCurrency(
																	group.subscriptionDeduction
																)}
															</div>
														)}
													</div>
													<ChevronDownIcon
														className={`w-4 h-4 text-indigo-500 flex-shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
													/>
												</div>

												{isExpanded && (
													<div className="tcr-payments__mobile-tcr-list">
														{group.tcrs.map(tcr => (
															<div
																key={tcr.tcrId}
																className="tcr-payments__mobile-tcr-item"
															>
																<div className="tcr-payments__mobile-tcr-top">
																	<span className="tcr-payments__tcr-id">
																		{tcr.tcrNumber}
																	</span>
																	<span className="text-xs text-gray-500">
																		{formatDateTime(
																			tcr.reviewedAt
																		)}
																	</span>
																</div>
																<div className="tcr-payments__mobile-tcr-row">
																	<span className="tcr-payments__mobile-tcr-label">
																		Service
																	</span>
																	<span className="tcr-payments__mobile-tcr-value">
																		{formatCurrency(
																			tcr.serviceAmount
																		)}
																	</span>
																</div>
																<div className="tcr-payments__mobile-tcr-row">
																	<span className="tcr-payments__mobile-tcr-label">
																		Net Payable
																	</span>
																	<span className="tcr-payments__mobile-tcr-value tcr-payments__mobile-tcr-value--net">
																		{formatCurrency(
																			tcr.netPayable
																		)}
																	</span>
																</div>
																{tcr.paymentStatus === 'PAID' && (
																	<div className="text-xs text-emerald-600 dark:text-emerald-400 font-semibold mt-2">
																		✅{' '}
																		{tcr.payoutMethod
																			? `Paid via ${tcr.payoutMethod}`
																			: 'Paid'}
																	</div>
																)}
															</div>
														))}

														{group.subscriptionDeductionItems.length >
															0 && (
															<div className="tcr-payments__mobile-tcr-item">
																{group.subscriptionDeductionItems.map(
																	item => (
																		<div
																			key={item.deductionId}
																			className="tcr-payments__mobile-tcr-row"
																		>
																			<span className="tcr-payments__mobile-tcr-label text-purple-600 dark:text-purple-400">
																				{item.description}
																			</span>
																			<span className="tcr-payments__mobile-tcr-value tcr-payments__mobile-tcr-value--fee">
																				{formatCurrency(
																					item.amount
																				)}
																			</span>
																		</div>
																	)
																)}
															</div>
														)}
													</div>
												)}

												{/* Pay button or paid label for mobile */}
												{group.allPaid ? (
													<div className="tcr-payments__mobile-paid-label">
														✅ All Paid
													</div>
												) : (
													<div className="tcr-payments__mobile-pay">
														<button
															className="tcr-payments__pay-btn tcr-payments__pay-btn--full cursor-pointer"
															onClick={() => openPayModal(group)}
														>
															💰{' '}
															{group.tcrCount > 1
																? `Pay All — ${formatCurrency(group.netPayable)}`
																: `Pay ${formatCurrency(group.netPayable)}`}
														</button>
													</div>
												)}
											</div>
										);
									})}
								</div>
							</>
						)}
					</>
				) : null}

				{/* Pay Modal */}
				<Modal
					isOpen={!!payModal}
					onClose={() => setPayModal(null)}
					title={payModal ? `Pay ${payModal.staffName}` : ''}
					size="sm"
				>
					{payModal && (
						<div className="tcr-payments__pay-modal">
							<p className="text-sm text-gray-500 dark:text-gray-400 text-center mb-4">
								{payModal.label}
							</p>

							{/* Amount display */}
							<div className="tcr-payments__pay-modal-amount">
								<div className="tcr-payments__pay-modal-amount-label">
									Net Amount Payable
								</div>
								<div className="tcr-payments__pay-modal-amount-value">
									{formatCurrency(payModal.netPayable)}
								</div>
								{payModal.subscriptionDeduction > 0 && (
									<div className="tcr-payments__pay-modal-amount-breakdown">
										Service {formatCurrency(payModal.serviceAmount)} − Fee{' '}
										{formatCurrency(payModal.subscriptionDeduction)}
									</div>
								)}
							</div>

							{/* Payment options */}
							<div className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
								Select Payment Method
							</div>
							<div className="space-y-3">
								<button
									className="tcr-payments__pay-option cursor-pointer"
									onClick={() => setPayModal(null)}
								>
									<div className="tcr-payments__pay-option-icon tcr-payments__pay-option-icon--bbp">
										💈
									</div>
									<div className="flex-1">
										<div className="tcr-payments__pay-option-name">via BBP</div>
										<div className="tcr-payments__pay-option-desc">
											Pay through Beauty Business Portal platform
										</div>
									</div>
									<ChevronRightIcon className="w-5 h-5 text-gray-300" />
								</button>
								<button
									className="tcr-payments__pay-option cursor-pointer"
									onClick={() => setPayModal(null)}
								>
									<div className="tcr-payments__pay-option-icon tcr-payments__pay-option-icon--cash">
										💵
									</div>
									<div className="flex-1">
										<div className="tcr-payments__pay-option-name">
											via Cash
										</div>
										<div className="tcr-payments__pay-option-desc">
											Record a cash payment made to staff
										</div>
									</div>
									<ChevronRightIcon className="w-5 h-5 text-gray-300" />
								</button>
								<button
									className="tcr-payments__pay-option cursor-pointer"
									onClick={() => setPayModal(null)}
								>
									<div className="tcr-payments__pay-option-icon tcr-payments__pay-option-icon--transfer">
										🏦
									</div>
									<div className="flex-1">
										<div className="tcr-payments__pay-option-name">
											via Other Transfer
										</div>
										<div className="tcr-payments__pay-option-desc">
											Zelle, CashApp, Venmo, or bank transfer
										</div>
									</div>
									<ChevronRightIcon className="w-5 h-5 text-gray-300" />
								</button>
							</div>
						</div>
					)}
				</Modal>
			</div>
		</AppLayout>
	);
};

export default TCRPayments;
